// Card de entidade de um canal na tela de Provedores do WL —
// border-l-4 na cor do estado (com provider=emerald, sem
// provider=neutro), ícone do canal, select do provider (opções do
// catálogo filtradas pelo canal), switch de ativo com write OTIMISTA
// e estado "sem provider" com CTA de associação.
import {
  MailIcon,
  MessageCircleIcon,
  SmartphoneIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import type {
  NotificationProvidersCatalogItemType,
  NotificationProvidersChannelType,
} from "@/types/notification-center"

import { useUpdateChannelProvider } from "../provider-mutations"

const providersPrefix =
  "pages.notificationCenter.providersPage"

// Ícone por canal (paleta semântica Pay2B).
const CHANNEL_ICONS: Record<
  NotificationProvidersChannelType["channel"],
  { Icon: typeof MailIcon; className: string }
> = {
  EMAIL: { Icon: MailIcon, className: "text-blue-500" },
  WHATSAPP: {
    Icon: MessageCircleIcon,
    className: "text-emerald-500",
  },
  SMS: {
    Icon: SmartphoneIcon,
    className: "text-violet-500",
  },
}

type ProviderChannelCardProps = {
  whiteLabelId: string
  channelProvider: NotificationProvidersChannelType
  catalog: NotificationProvidersCatalogItemType[]
}

export const ProviderChannelCard = ({
  whiteLabelId,
  channelProvider,
  catalog,
}: ProviderChannelCardProps) => {
  const { t } = useTranslation()
  const { channel, provider, active } = channelProvider
  const updateChannelProvider =
    useUpdateChannelProvider(whiteLabelId)
  const { Icon, className } = CHANNEL_ICONS[channel]
  const hasProvider = Boolean(provider)

  // Opções do catálogo global filtradas pelo canal do card.
  const channelOptions = catalog.filter(
    (item) => item.channel === channel
  )

  return (
    <li
      data-testid={`provider-channel-${channel}`}
      className={cn(
        "flex flex-col gap-4 rounded-xl border border-l-4 bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between",
        hasProvider
          ? "border-l-emerald-500"
          : "border-l-zinc-300"
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Icon
            className={cn("size-5", className)}
            aria-hidden="true"
          />
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold">
            {channel}
          </span>
          {hasProvider ? (
            <Badge
              variant="outline"
              className={cn(
                "w-fit rounded-full border font-medium",
                active
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-zinc-200 bg-zinc-50 text-zinc-500"
              )}
            >
              {provider}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">
              {t(`${providersPrefix}.channels.noProvider`)}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Select
          value={provider ?? undefined}
          onValueChange={(nextProvider) =>
            updateChannelProvider.mutate({
              channel,
              payload: {
                provider: nextProvider,
                active,
              },
            })
          }
        >
          <SelectTrigger
            className="w-56"
            aria-label={t(
              `${providersPrefix}.channels.selectProvider`,
              { channel }
            )}
          >
            <SelectValue
              placeholder={t(
                `${providersPrefix}.channels.associateCta`
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {channelOptions.map((option) => (
              <SelectItem
                key={option.id}
                value={option.name}
              >
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch
            checked={active}
            // Sem provider não há o que ativar.
            disabled={!hasProvider}
            aria-label={t(
              `${providersPrefix}.channels.toggleActive`,
              { channel }
            )}
            onCheckedChange={(nextActive) =>
              updateChannelProvider.mutate({
                channel,
                payload: {
                  provider,
                  active: nextActive,
                },
              })
            }
          />
          <span className="text-xs text-muted-foreground">
            {t(
              `${providersPrefix}.channels.${active ? "active" : "inactive"}`
            )}
          </span>
        </div>
      </div>
    </li>
  )
}
